import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import StatusPill from '@/components/seop/StatusPill';
import PercentInput from '@/components/seop/PercentInput';
import UtilizationBar from '@/components/seop/UtilizationBar';
import { useToast } from '@/contexts/ToastContext';
import { useSeopMonth } from '@/hooks/useSeopMonth';
import { seopService } from '@/services/seop.service';
import type {
  SeopAutoBalanceResult,
  SeopBands,
  SeopCalc,
  SeopDetail,
  SeopMetric,
  SeopMixRow,
  SeopMonth,
  SeopProduct,
} from '@/types/seop';
import { redistributeShare } from '@/utils/seopAllocation';
import BandsEditor from '@/components/seop/BandsEditor';
import { fmtHours, fmtUnits, fmtUtil, seopNum, seopStatus } from '@/utils/seopFormat';

const CELL = 'py-3 pr-3 text-sm text-content-primary';
const NUM = `${CELL} text-right tabular-nums`;

const DetailRows: FC<{ rows: SeopDetail[] }> = ({ rows }) => (
  <tr className="bg-surface-secondary/40">
    <td colSpan={9} className="px-4 py-3">
      {rows.length === 0 ? (
        <span className="text-sm text-content-muted">Sem carga alocada neste recurso no período.</span>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-content-muted">
              <th className="pb-2 text-left">Produto</th>
              <th className="pb-2 text-left">Etapa</th>
              <th className="pb-2 text-right">Unidades</th>
              <th className="pb-2 text-right">Lotes</th>
              <th className="pb-2 text-right">Horas</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d, i) => (
              <tr key={`${d.code}-${d.stage}-${i}`} className="border-t border-theme-border/50">
                <td className="py-1.5 text-content-primary">
                  {d.code} — {d.description}
                </td>
                <td className="py-1.5 text-content-secondary">{d.stage}</td>
                <td className="py-1.5 text-right tabular-nums">{fmtUnits(seopNum(d.allocated_units))}</td>
                <td className="py-1.5 text-right tabular-nums">{fmtUnits(seopNum(d.lots))}</td>
                <td className="py-1.5 text-right tabular-nums">{fmtHours(seopNum(d.hours))} h</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </td>
  </tr>
);

const CapacityRow: FC<{
  metric: SeopMetric;
  details: SeopDetail[];
  expanded: boolean;
  bands: SeopBands;
  onToggle: () => void;
}> = ({ metric, details, expanded, bands, onToggle }) => {
  const util = seopNum(metric.util);
  const { label, tone } = seopStatus(util, bands);
  return (
    <>
      <tr
        className="cursor-pointer border-b border-theme-border/50 hover:bg-surface-hover/50"
        onClick={onToggle}
        data-testid={`seop-recurso-${metric.name}`}
      >
        <td className={`${CELL} text-content-secondary`}>{metric.critical}</td>
        <td className={CELL}>
          <span className="inline-flex items-center gap-1 font-semibold">
            <span className="material-symbols-outlined text-base text-content-muted">
              {expanded ? 'expand_less' : 'expand_more'}
            </span>
            {metric.name}
          </span>
        </td>
        <td className={NUM}>{fmtUnits(metric.allocated_units)}</td>
        <td className={NUM}>{fmtUnits(metric.product_count)}</td>
        <td className={NUM}>{fmtHours(metric.load)} h</td>
        <td className={NUM}>{fmtHours(metric.capacity)} h</td>
        <td className={NUM}>{fmtHours(metric.slack)} h</td>
        <td className={`${CELL} text-right`}>
          <UtilizationBar util={util} bands={bands} />
        </td>
        <td className={CELL}>
          <StatusPill label={label} tone={tone} withDot />
        </td>
      </tr>
      {expanded && <DetailRows rows={details} />}
    </>
  );
};

const MixRowView: FC<{
  row: SeopMixRow;
  saving: boolean;
  onCommit: (row: SeopMixRow, resource: string, pct: number) => void;
}> = ({ row, saving, onCommit }) => {
  const soma = row.options.reduce((s, o) => s + seopNum(o.share), 0);
  const desbalanceada = Math.abs(soma - 1) > 0.001;
  return (
    <tr className="border-b border-theme-border/50 align-top">
      <td className={CELL}>{row.code}</td>
      <td className={`${CELL} max-w-xs truncate`} title={row.description}>
        {row.description}
      </td>
      <td className={`${CELL} text-content-secondary`}>{row.stage}</td>
      <td className={NUM}>{fmtUnits(seopNum(row.demand))}</td>
      <td className={CELL}>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {row.options.map((o) => (
            <span key={o.resource} className="inline-flex items-center gap-2 text-sm">
              <span className="text-content-secondary">{o.resource}</span>
              {row.single ? (
                <span className="text-content-muted">100% · recurso único</span>
              ) : (
                <>
                  <PercentInput
                    value={Math.round(seopNum(o.share) * 100)}
                    disabled={saving}
                    label={`${row.code} · ${row.stage} · ${o.resource}`}
                    className="w-16 rounded-lg border border-theme-border bg-surface-secondary px-2 py-1 text-right text-sm disabled:opacity-50"
                    onCommit={(campo) => onCommit(row, o.resource, Number(campo))}
                  />
                  <span className="text-content-muted">
                    % · {fmtUnits(seopNum(o.allocated_units))} un.
                  </span>
                </>
              )}
            </span>
          ))}
        </div>
      </td>
      <td className={NUM}>
        <span className={desbalanceada ? 'text-theme-danger font-semibold' : 'text-content-muted'}>
          {fmtUtil(soma)}
        </span>
      </td>
    </tr>
  );
};

const AutoBalanceSummary: FC<{ result: SeopAutoBalanceResult }> = ({ result }) => {
  const antes = result.score_before;
  const depois = result.score_after;
  return (
    <div
      data-testid="seop-auto-balance-resultado"
      className="mt-3 rounded-lg border border-theme-border bg-surface-secondary p-4 text-sm text-content-secondary"
    >
      {result.improvements === 0 ? (
        <p>
          <strong className="text-content-primary">Nenhuma melhoria encontrada.</strong> O mix já está
          no melhor ponto que a heurística alcança neste período. As alocações derivadas do mix foram
          gravadas assim mesmo ({fmtUnits(result.overrides_written)} no total) — a partir de agora elas
          ficam fixas até alguém mudar.
        </p>
      ) : (
        <p>
          <strong className="text-content-primary">
            {fmtUnits(result.improvements)} ajuste{result.improvements === 1 ? '' : 's'}
          </strong>{' '}
          aplicado{result.improvements === 1 ? '' : 's'} · {fmtUnits(result.overrides_written)} alocações
          gravadas.
        </p>
      )}
      <p className="mt-2 tabular-nums">
        Maior utilização {fmtUtil(seopNum(antes.max))} → {fmtUtil(seopNum(depois.max))} · Recursos acima
        de 100%: {antes.over} → {depois.over} · Excesso somado {fmtUtil(seopNum(antes.total_over))} →{' '}
        {fmtUtil(seopNum(depois.total_over))}
      </p>
    </div>
  );
};

const SeopBalancePage: FC = () => {
  const { month } = useSeopMonth();
  const { showToast } = useToast();
  const [calc, setCalc] = useState<SeopCalc | null>(null);
  const [products, setProducts] = useState<SeopProduct[]>([]);
  const [months, setMonths] = useState<SeopMonth[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [autoResult, setAutoResult] = useState<SeopAutoBalanceResult | null>(null);

  const fetchCalc = useCallback(async (mes: number) => {
    setCalc(await seopService.getCalc(mes));
  }, []);

  /** Grava uma faixa e recarrega o mês: o rótulo de cada linha vem do servidor. */
  const salvarFaixas = useCallback(
    async (mudanca: Partial<SeopBands>) => {
      setSaving(true);
      try {
        await seopService.updateThresholds(mudanca);
        await fetchCalc(month);
      } catch {
        // A ordem (balanceado ≤ atenção ≤ crítico) é validada no servidor — é a recusa
        // que chega aqui, e é ela que a pessoa precisa ler.
        showToast('Faixa inválida. Elas têm que crescer: balanceado ≤ atenção ≤ crítico.', 'error');
        await fetchCalc(month);
      } finally {
        setSaving(false);
      }
    },
    [fetchCalc, month, showToast],
  );

  const fetchAll = useCallback(
    async (mes: number) => {
      setIsLoading(true);
      setError(null);
      try {
        const [, listaProdutos, listaMeses] = await Promise.all([
          fetchCalc(mes),
          seopService.getProducts(),
          seopService.getMonths(),
        ]);
        setProducts(listaProdutos);
        setMonths(listaMeses);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Erro desconhecido');
      } finally {
        setIsLoading(false);
      }
    },
    [fetchCalc],
  );

  useEffect(() => {
    setAutoResult(null);
    fetchAll(month);
  }, [month, fetchAll]);

  const detailsByResource = useMemo(() => {
    const mapa = new Map<string, SeopDetail[]>();
    for (const d of calc?.details ?? []) {
      const lista = mapa.get(d.resource);
      if (lista) lista.push(d);
      else mapa.set(d.resource, [d]);
    }
    return mapa;
  }, [calc]);

  const commitShare = useCallback(
    async (row: SeopMixRow, resource: string, pct: number) => {
      const produto = products.find((p) => p.code === row.code);
      const mes = months.find((m) => m.index === month);
      if (!produto || !mes) {
        showToast('Não achei o produto ou o mês no cadastro. Recarregue a página.', 'error');
        return;
      }
      const atual = Object.fromEntries(row.options.map((o) => [o.resource, seopNum(o.share)]));
      const recursos = row.options.map((o) => o.resource);
      setSaving(true);
      try {
        await seopService.putAllocationOverride({
          product: produto.id,
          stage: row.stage,
          month: mes.id,
          allocations: redistributeShare(atual, recursos, resource, pct),
        });
        await fetchCalc(month);
      } catch {
        showToast('Não consegui gravar o mix. Tente de novo.', 'error');
      } finally {
        setSaving(false);
      }
    },
    [products, months, month, showToast, fetchCalc],
  );

  const runAutoBalance = useCallback(async () => {
    setSaving(true);
    try {
      const resultado = await seopService.postAutoBalance(month);
      setAutoResult(resultado);
      await fetchCalc(month);
    } catch {
      showToast('O balanceamento automático falhou. Tente de novo.', 'error');
    } finally {
      setSaving(false);
    }
  }, [month, showToast, fetchCalc]);

  if (isLoading) {
    return <div className="p-6 lg:p-10 text-content-muted">Calculando o período…</div>;
  }

  if (error || !calc) {
    return (
      <div className="p-6 lg:p-10">
        <div className="rounded-lg border border-theme-danger/40 bg-theme-danger/10 p-4 text-content-primary">
          Não foi possível calcular o período. {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-content-muted">
          Redistribuição de mix entre recursos compatíveis, mantendo 100% da demanda da etapa.
        </p>
        <button
          type="button"
          onClick={runAutoBalance}
          disabled={saving}
          data-testid="seop-auto-balance"
          className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-contrast transition hover:bg-accent-hover disabled:opacity-50"
        >
          Balancear automaticamente
        </button>
      </div>

      {autoResult && <AutoBalanceSummary result={autoResult} />}

      <div className="mt-4 rounded-xl border border-theme-border bg-surface-card p-5">
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="font-semibold text-content-primary">Carga × capacidade</h3>
          <BandsEditor bands={calc.bands} disabled={saving} onCommit={salvarFaixas} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-theme-border text-xs uppercase tracking-wide text-content-muted">
                <th className="pb-2 pr-3 text-left">Área crítica</th>
                <th className="pb-2 pr-3 text-left">Recurso</th>
                <th className="pb-2 pr-3 text-right">Qtd. alocada</th>
                <th className="pb-2 pr-3 text-right">Produtos</th>
                <th className="pb-2 pr-3 text-right">Carga</th>
                <th className="pb-2 pr-3 text-right">Capacidade</th>
                <th className="pb-2 pr-3 text-right">Folga</th>
                <th className="pb-2 pr-3 text-right">Utilização</th>
                <th className="pb-2 pr-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {calc.metrics.map((m) => (
                <CapacityRow
                  key={m.name}
                  metric={m}
                  bands={calc.bands}
                  details={detailsByResource.get(m.name) ?? []}
                  expanded={expanded === m.name}
                  onToggle={() => setExpanded(expanded === m.name ? null : m.name)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-theme-border bg-surface-card p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold text-content-primary">Mix por produto e etapa</h3>
          <span className="rounded-full border border-theme-border px-2 py-0.5 text-xs text-content-muted">
            {calc.mix.length} linha{calc.mix.length === 1 ? '' : 's'}
          </span>
        </div>
        <p className="mb-3 text-sm text-content-muted">
          Etapa com um recurso só aparece aqui como 100% fixo — o ajuste manual existe quando há duas
          ou mais alternativas. O valor é gravado ao sair do campo.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-theme-border text-xs uppercase tracking-wide text-content-muted">
                <th className="pb-2 pr-3 text-left">Código</th>
                <th className="pb-2 pr-3 text-left">Produto</th>
                <th className="pb-2 pr-3 text-left">Etapa</th>
                <th className="pb-2 pr-3 text-right">Demanda</th>
                <th className="pb-2 pr-3 text-left">Alternativas</th>
                <th className="pb-2 pr-3 text-right">Soma</th>
              </tr>
            </thead>
            <tbody>
              {calc.mix.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-4 text-sm text-content-muted">
                    Nenhum produto com demanda e roteiro no período selecionado.
                  </td>
                </tr>
              ) : (
                calc.mix.map((row) => (
                  <MixRowView
                    key={`${row.code}-${row.stage}`}
                    row={row}
                    saving={saving}
                    onCommit={commitShare}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SeopBalancePage;
